import { use, useMemo } from 'react';
import { useTiers, useTopTier } from 'stores/useCreateModifyStore';

import type { CreateModifyContextInterface } from 'contexts/CreateModifyContext';

import { CreateModifyContext } from 'contexts/CreateModifyContext';

/*
 * TODO: (ED-19491) Hot/reactive consumers should call the atomic hooks (useTiers/useTopTier) and
 * useCreateModifyActions directly for true selector isolation. This aggregator subscribes every
 * consumer to both tiers and topTier, so any tier change re-renders them all (same blast radius as
 * the old React Context).
 */
export const useCreateModify = <
  TData extends Record<string, any> = any,
  TMetadata extends Record<string, any> = any,
  TSeededData extends Record<string, any> = any,
>(): CreateModifyContextInterface<TData, TMetadata, TSeededData> => {
  const context = use(CreateModifyContext);
  /*
   * tiers/topTier come from the store, not context. The atomic hooks return the base record type, so
   * the per-consumer generics (TData/TMetadata/TSeededData) are re-narrowed here via a cast.
   */
  const tiers = useTiers() as CreateModifyContextInterface<TData, TMetadata, TSeededData>['tiers'];
  const topTier = useTopTier() as CreateModifyContextInterface<TData, TMetadata, TSeededData>['topTier'];

  const merged = useMemo(() => (context ? { ...context, tiers, topTier } : undefined), [context, tiers, topTier]);

  if (!merged) {
    throw new Error('useCreateModify must be used within a CreateModifyProvider');
  }

  return merged;
};
